import { useEffect, useRef, useState } from 'react'
import { Box, Flex, Text } from '@chakra-ui/react'
import { layersMappingToName, LayerT } from './layers'
import { MapLayersColours } from 'modules/BioriskMpox/common/GraphColors'
import { ArrowDown } from '@carbon/icons-react'

interface LegendItemProps {
  handleClick: (item: LayerT) => void
  index: number
  item: string
  isSelected: boolean
  colour: string
}

const LegendItem = ({
  handleClick,
  index,
  item,
  isSelected,
  colour,
}: LegendItemProps) => (
  <Box
    key={index}
    display='flex'
    py={1}
    px={3}
    gap={4}
    alignItems='center'
    cursor='pointer'
    backgroundColor={isSelected ? 'gray.200' : ''}
    rounded='md'
    _hover={{
      backgroundColor: 'gray.100',
    }}
    onClick={() => handleClick(item as LayerT)}
  >
    <Box
      border='3px solid'
      bgColor={`${colour}b8`}
      borderColor={colour}
      width='16px'
      height='16px'
      rounded='full'
    />
    <Text textTransform='capitalize' fontSize='sm'>
      {layersMappingToName[item] ?? item}
    </Text>
  </Box>
)

export default function Legend({
  handleClick,
  selectedLayer,
  disease,
  mapLayers,
}: {
  handleClick: (e: LayerT) => void
  selectedLayer: LayerT
  disease?: string
  mapLayers?: string[]
}) {
  const isMpox = disease === 'mpox'
  const [showIndicator, setShowIndicator] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = scrollRef.current
    if (el && el.scrollHeight > el.clientHeight) {
      setShowIndicator((mapLayers?.length ?? 0) > 5)
    } else {
      setShowIndicator(false)
    }

    const handleScroll = () => {
      setShowIndicator(false)
    }

    el?.addEventListener('scroll', handleScroll)
    return () => el?.removeEventListener('scroll', handleScroll)
  }, [mapLayers])

  return (
    <Box position='absolute' bottom={3} left={3} zIndex={10}>
      <Box
        ref={scrollRef}
        backgroundColor='white'
        borderRadius='md'
        shadow='md'
        px={1}
        py={1}
        maxH='200px'
        overflowY='auto'
        position='relative'
      >
        {mapLayers?.map((item, index) => {
          if (item === 'countries') return null
          return (
            <LegendItem
              key={item}
              index={index}
              item={item}
              colour={
                MapLayersColours.specific[item] ??
                MapLayersColours.extra[index % MapLayersColours.extra.length] ??
                '#808080'
              }
              isSelected={item === selectedLayer}
              handleClick={handleClick}
            />
          )
        })}
        {isMpox && (
          <LegendItem
            index={(mapLayers?.length ?? 0) + 1}
            item={'clade'}
            colour={'#B565A7'}
            isSelected={'clade' === selectedLayer}
            handleClick={handleClick}
          />
        )}
        {showIndicator && (
          <Flex
            position='absolute'
            bottom={1}
            left='50%'
            transform='translateX(-50%)'
            bg='white'
            border='1px solid'
            borderColor={'gray.300'}
            px={2}
            py={1}
            borderRadius='md'
            boxShadow='xl'
            alignItems={'center'}
            gap={2}
          >
            <Text fontSize='md' color='black'>
              Scroll
            </Text>
            <ArrowDown color='black' />
          </Flex>
        )}
      </Box>
    </Box>
  )
}
